import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, Input } from '../components/ui';
import { useAppDispatch } from '../store';
import {
    fetchMicrosites,
    fetchMicrositeStats,
    fetchSortingFilters,
    useMicrositesList,
    useMicrositesStats,
    useMicrositesLoading,
    useMicrositesError,
    useMicrositesPagination,
    setSorting,
    clearFilters,
} from '../store/slices/micrositesSlice';
import { MICROSITE_STATUS, DEFAULT_SORT, PAGINATION_DEFAULTS } from '../constants/api.constants';
import type { Status } from '../types/common';
import type { MicrositeListParams, MicrositeListItem } from '../types/api.types';

/**
 * Microsites management page with metrics, search, and list view
 */
type SortOption = {
    value: string;
    label: string;
};

export default function Microsites() {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    // Redux state
    const microsites = useMicrositesList();
    const stats = useMicrositesStats();
    const loading = useMicrositesLoading();
    const error = useMicrositesError();
    const pagination = useMicrositesPagination();

    // Local UI state
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedStatuses, setSelectedStatuses] = useState<Status[]>([]);
    const [showFilterMenu, setShowFilterMenu] = useState(false);
    const [showSortMenu, setShowSortMenu] = useState(false);
    const [currentSortValue, setCurrentSortValue] = useState<string>(DEFAULT_SORT);
    const [currentSkip, setCurrentSkip] = useState(0);

    // State for sorting/filter options from API
    const [availableSortOptions, setAvailableSortOptions] = useState<SortOption[]>([]);
    const [availableStatusFilters, setAvailableStatusFilters] = useState<Status[]>([]);

    // Fetch sorting and filter options on mount
    useEffect(() => {
        const fetchOptions = async () => {
            const result = await dispatch(fetchSortingFilters()).unwrap();

            // Convert sorting object to array of options
            const sortOpts: SortOption[] = Object.keys(result.sorting).map((key) => ({
                value: key,
                label: key,
            }));
            setAvailableSortOptions(sortOpts);

            // Set available status filters
            setAvailableStatusFilters(result.filters.status as Status[]);
        };

        fetchOptions();
    }, [dispatch]);

    const loadData = () => {
        const params: MicrositeListParams = {
            skip: currentSkip,
            limit: PAGINATION_DEFAULTS.LIMIT,
            sortValue: currentSortValue,
        };

        // Add filters if any
        if (selectedStatuses.length > 0 && selectedStatuses.length === 1) {
            params.status = selectedStatuses[0];
        }
        if (searchQuery) {
            params.search = searchQuery;
        }

        dispatch(fetchMicrosites(params));
        dispatch(fetchMicrositeStats());
    };

    // Reload when filters/sorting/pagination changes
    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSkip, currentSortValue, selectedStatuses, searchQuery]);

    const toggleStatusFilter = (status: Status) => {
        setSelectedStatuses((prev) =>
            prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
        );
        setCurrentSkip(0); // Reset to first page when filter changes
    };

    const handleClearFilters = () => {
        setSelectedStatuses([]);
        setSearchQuery('');
        setCurrentSkip(0);
        dispatch(clearFilters());
    };

    const handleSortChange = (sortValue: string) => {
        setCurrentSortValue(sortValue);
        setCurrentSkip(0); // Reset to first page when sort changes
        dispatch(setSorting({ sortValue }));
        setShowSortMenu(false);
    };

    // Build status options dynamically from API data
    const getStatusColor = (status: Status) => {
        switch (status) {
            case MICROSITE_STATUS.PUBLISHED:
                return 'bg-[#10b981] text-white';
            case MICROSITE_STATUS.DRAFT:
                return 'bg-[#f59e0b] text-white';
            case MICROSITE_STATUS.INACTIVE:
                return 'bg-[#64748b] text-white';
            default:
                return 'bg-[#e2e8f0] text-[#64748b]';
        }
    };

    const getStatusDisplayLabel = (status: Status) => {
        switch (status) {
            case MICROSITE_STATUS.PUBLISHED:
                return 'Live';
            case MICROSITE_STATUS.DRAFT:
                return 'Draft';
            case MICROSITE_STATUS.INACTIVE:
                return 'Inactive';
            default:
                return status;
        }
    };

    const getStatusCount = (status: Status) => {
        switch (status) {
            case MICROSITE_STATUS.PUBLISHED:
                return stats?.live || 0;
            case MICROSITE_STATUS.DRAFT:
                return stats?.draft || 0;
            case MICROSITE_STATUS.INACTIVE:
                return stats?.inactive || 0;
            default:
                return 0;
        }
    };

    const statusOptions = availableStatusFilters.map((status) => ({
        value: status,
        label: getStatusDisplayLabel(status),
        count: getStatusCount(status),
        color: getStatusColor(status),
    }));

    const handleSearchChange = (value: string) => {
        setSearchQuery(value);
        setCurrentSkip(0); // Reset to first page when search changes
    };

    return (
        <div className="w-full space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-4xl font-bold text-[#1e293b] leading-tight">Microsites</h2>
                    <p className="text-lg text-[#64748b] mt-2">
                        Manage all your store microsites in one place
                    </p>
                </div>
                <button
                    onClick={() => navigate('/microsites/create')}
                    className="px-6 py-3 bg-gradient-to-r from-[#6e64f0] to-[#8b7cf6] text-white rounded-lg font-semibold hover:shadow-lg transition-all duration-200 text-base"
                >
                    + Create New Microsite
                </button>
            </div>

            {/* Metrics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                    <CardContent className="py-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-base text-[#64748b] mb-1">Total Microsites</p>
                                <p className="text-4xl font-bold text-[#1e293b]">
                                    {stats?.total || 0}
                                </p>
                            </div>
                            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#6e64f0] to-[#8b7cf6] flex items-center justify-center">
                                <svg
                                    className="w-6 h-6 text-white"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                    />
                                </svg>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="py-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-base text-[#64748b] mb-1">Live Microsites</p>
                                <p className="text-4xl font-bold text-[#10b981]">
                                    {stats?.live || 0}
                                </p>
                            </div>
                            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#10b981] to-[#34d399] flex items-center justify-center">
                                <svg
                                    className="w-6 h-6 text-white"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M5 13l4 4L19 7"
                                    />
                                </svg>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="py-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-base text-[#64748b] mb-1">Draft</p>
                                <p className="text-4xl font-bold text-[#f59e0b]">
                                    {stats?.draft || 0}
                                </p>
                            </div>
                            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#f59e0b] to-[#fbbf24] flex items-center justify-center">
                                <svg
                                    className="w-6 h-6 text-white"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                    />
                                </svg>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="py-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-base text-[#64748b] mb-1">Inactive</p>
                                <p className="text-4xl font-bold text-[#64748b]">
                                    {stats?.inactive || 0}
                                </p>
                            </div>
                            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#64748b] to-[#94a3b8] flex items-center justify-center">
                                <svg
                                    className="w-6 h-6 text-white"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                                    />
                                </svg>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Search and Filter Bar */}
            <div className="flex flex-col md:flex-row items-stretch md:items-center gap-4">
                <div className="flex-1 relative">
                    <svg
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#64748b]"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                    </svg>
                    <Input
                        type="text"
                        placeholder="Search by store name, brand, or URL..."
                        value={searchQuery}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        className="pl-10"
                    />
                </div>

                {/* Sort Dropdown */}
                <div className="relative">
                    <button
                        onClick={() => setShowSortMenu(!showSortMenu)}
                        className="px-4 py-2.5 border border-[#e2e8f0] rounded-lg transition-all flex items-center gap-2 text-[#64748b] hover:bg-[#f8fafc] w-full md:w-auto"
                    >
                        <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"
                            />
                        </svg>
                        <span className="font-medium text-sm whitespace-nowrap overflow-hidden text-ellipsis max-w-[150px]">
                            {currentSortValue}
                        </span>
                        <svg
                            className="w-4 h-4 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                            />
                        </svg>
                    </button>

                    {/* Sort Dropdown Menu */}
                    {showSortMenu && (
                        <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-[#e2e8f0] z-10">
                            <div className="p-2">
                                {availableSortOptions.map((option, index) => (
                                    <button
                                        key={option.value}
                                        onClick={() => handleSortChange(option.value)}
                                        className={`w-full text-left px-4 py-2.5 rounded-lg transition-all flex items-center justify-between ${
                                            currentSortValue === option.value
                                                ? 'bg-[#f1f5f9] text-[#6e64f0] font-medium'
                                                : 'text-[#64748b] hover:bg-[#f8fafc]'
                                        } ${index > 0 && index % 2 === 0 ? 'mt-1 pt-3 border-t border-[#f1f5f9]' : ''}`}
                                    >
                                        <span className="text-sm">{option.label}</span>
                                        {currentSortValue === option.value && (
                                            <svg
                                                className="w-4 h-4 flex-shrink-0"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M5 13l4 4L19 7"
                                                />
                                            </svg>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Filter Dropdown */}
                <div className="relative">
                    <button
                        onClick={() => setShowFilterMenu(!showFilterMenu)}
                        className={`px-4 py-2.5 border rounded-lg transition-all flex items-center gap-2 ${
                            selectedStatuses.length > 0
                                ? 'bg-[#6e64f0] text-white border-[#6e64f0]'
                                : 'border-[#e2e8f0] text-[#64748b] hover:bg-[#f8fafc]'
                        }`}
                    >
                        <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                            />
                        </svg>
                        <span className="font-medium">
                            Filter{selectedStatuses.length > 0 && ` (${selectedStatuses.length})`}
                        </span>
                    </button>

                    {/* Filter Dropdown */}
                    {showFilterMenu && (
                        <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-xl border border-[#e2e8f0] z-10">
                            <div className="p-4 border-b border-[#e2e8f0]">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-base font-semibold text-[#1e293b]">
                                        Filters
                                    </h3>
                                    {selectedStatuses.length > 0 && (
                                        <button
                                            onClick={handleClearFilters}
                                            className="text-sm text-[#6e64f0] hover:underline font-medium"
                                        >
                                            Clear all
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="p-4">
                                <p className="text-sm font-medium text-[#64748b] mb-3">Status</p>
                                <div className="space-y-2">
                                    {statusOptions.map((option) => (
                                        <label
                                            key={option.value}
                                            className="flex items-center gap-3 p-3 rounded-lg hover:bg-[#f8fafc] cursor-pointer transition-all group border border-transparent hover:border-[#e2e8f0]"
                                        >
                                            <div className="relative flex items-center">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedStatuses.includes(
                                                        option.value
                                                    )}
                                                    onChange={() =>
                                                        toggleStatusFilter(option.value)
                                                    }
                                                    className="sr-only peer"
                                                />
                                                <div
                                                    className={`w-5 h-5 rounded border-2 transition-all flex items-center justify-center ${
                                                        selectedStatuses.includes(option.value)
                                                            ? 'bg-[#6e64f0] border-[#6e64f0]'
                                                            : 'bg-white border-[#cbd5e1] group-hover:border-[#6e64f0]'
                                                    }`}
                                                >
                                                    {selectedStatuses.includes(option.value) && (
                                                        <svg
                                                            className="w-3 h-3 text-white"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={3}
                                                                d="M5 13l4 4L19 7"
                                                            />
                                                        </svg>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 flex-1">
                                                <div
                                                    className={`w-2.5 h-2.5 rounded-full ${option.color} shadow-sm`}
                                                ></div>
                                                <span className="text-sm font-medium text-[#1e293b]">
                                                    {option.label}
                                                </span>
                                            </div>
                                            <span className="text-sm text-[#64748b] font-medium bg-[#f1f5f9] px-2 py-0.5 rounded">
                                                {option.count}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Active Filters Display */}
            {selectedStatuses.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-[#64748b]">Active filters:</span>
                    {selectedStatuses.map((status) => {
                        const option = statusOptions.find((o) => o.value === status);
                        return (
                            <span
                                key={status}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#f1f5f9] text-[#1e293b] rounded-full text-sm"
                            >
                                <div className={`w-2 h-2 rounded-full ${option?.color}`}></div>
                                {option?.label}
                                <button
                                    onClick={() => toggleStatusFilter(status)}
                                    className="ml-1 hover:text-[#ef4444] transition-colors"
                                >
                                    <svg
                                        className="w-3.5 h-3.5"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M6 18L18 6M6 6l12 12"
                                        />
                                    </svg>
                                </button>
                            </span>
                        );
                    })}
                </div>
            )}

            {/* Error Message */}
            {error && (
                <div className="bg-[#fee2e2] border border-[#ef4444] text-[#ef4444] px-4 py-3 rounded-lg">
                    <p className="text-sm font-medium">{error}</p>
                </div>
            )}

            {/* Loading State */}
            {loading === 'loading' && (
                <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6e64f0]"></div>
                </div>
            )}

            {/* Microsites List */}
            {loading !== 'loading' && (
                <Card>
                    <CardContent className="p-0">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#64748b] uppercase tracking-wider">
                                            Store Details
                                        </th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#64748b] uppercase tracking-wider">
                                            URL
                                        </th>
                                        <th className="px-6 py-4 text-center text-sm font-semibold text-[#64748b] uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#64748b] uppercase tracking-wider">
                                            Created At
                                        </th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#64748b] uppercase tracking-wider">
                                            Updated At
                                        </th>
                                        <th className="px-6 py-4 text-center text-sm font-semibold text-[#64748b] uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-[#e2e8f0]">
                                    {microsites.length > 0 ? (
                                        microsites.map((site: MicrositeListItem) => (
                                            <tr
                                                key={site._id}
                                                className="hover:bg-[#f8fafc] transition-colors"
                                            >
                                                <td className="px-6 py-4">
                                                    <div>
                                                        <p className="text-base font-semibold text-[#1e293b]">
                                                            {site.storeName}
                                                        </p>
                                                        <p className="text-sm text-[#64748b] mt-1">
                                                            {site.brandName}
                                                        </p>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <a
                                                        href={site.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-base text-[#6e64f0] hover:underline flex items-center gap-1"
                                                    >
                                                        {site.url}
                                                        <svg
                                                            className="w-3 h-3"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                                            />
                                                        </svg>
                                                    </a>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <span
                                                        className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${getStatusColor(site.status)}`}
                                                    >
                                                        {getStatusDisplayLabel(site.status)}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div>
                                                        <p className="text-base text-[#1e293b]">
                                                            {new Date(
                                                                site.createdAt
                                                            ).toLocaleDateString('en-US', {
                                                                year: 'numeric',
                                                                month: 'short',
                                                                day: 'numeric',
                                                            })}
                                                        </p>
                                                        <p className="text-sm text-[#64748b] mt-0.5">
                                                            {new Date(
                                                                site.createdAt
                                                            ).toLocaleTimeString('en-US', {
                                                                hour: '2-digit',
                                                                minute: '2-digit',
                                                            })}
                                                        </p>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div>
                                                        <p className="text-base text-[#1e293b]">
                                                            {new Date(
                                                                site.updatedAt
                                                            ).toLocaleDateString('en-US', {
                                                                year: 'numeric',
                                                                month: 'short',
                                                                day: 'numeric',
                                                            })}
                                                        </p>
                                                        <p className="text-sm text-[#64748b] mt-0.5">
                                                            {new Date(
                                                                site.updatedAt
                                                            ).toLocaleTimeString('en-US', {
                                                                hour: '2-digit',
                                                                minute: '2-digit',
                                                            })}
                                                        </p>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center justify-center gap-2">
                                                        <button
                                                            onClick={() => {
                                                                navigate(
                                                                    `/microsites/edit/${site._id}`
                                                                );
                                                            }}
                                                            className="p-2 hover:bg-[#e2e8f0] rounded transition-colors"
                                                            title="Edit"
                                                        >
                                                            <svg
                                                                className="w-5 h-5 text-[#64748b]"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                viewBox="0 0 24 24"
                                                            >
                                                                <path
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    strokeWidth={2}
                                                                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                                                />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={6} className="px-6 py-12 text-center">
                                                <div className="flex flex-col items-center justify-center">
                                                    <svg
                                                        className="w-12 h-12 text-[#cbd5e1] mb-3"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                                        />
                                                    </svg>
                                                    <p className="text-[#64748b] font-medium">
                                                        No microsites found
                                                    </p>
                                                    <p className="text-sm text-[#94a3b8] mt-1">
                                                        Try adjusting your search or filters
                                                    </p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Pagination Info */}
            {pagination && microsites.length > 0 && (
                <div className="flex items-center justify-between text-sm text-[#64748b]">
                    <p>
                        Showing{' '}
                        <span className="font-medium text-[#1e293b]">
                            {pagination.skip + 1}-
                            {Math.min(pagination.skip + pagination.limit, pagination.total)}
                        </span>{' '}
                        of <span className="font-medium text-[#1e293b]">{pagination.total}</span>{' '}
                        microsites
                    </p>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() =>
                                setCurrentSkip(Math.max(0, currentSkip - PAGINATION_DEFAULTS.LIMIT))
                            }
                            disabled={currentSkip === 0}
                            className="px-4 py-2 border border-[#e2e8f0] rounded-lg hover:bg-[#f8fafc] disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Previous
                        </button>
                        <button
                            onClick={() => setCurrentSkip(currentSkip + PAGINATION_DEFAULTS.LIMIT)}
                            disabled={currentSkip + PAGINATION_DEFAULTS.LIMIT >= pagination.total}
                            className="px-4 py-2 border border-[#e2e8f0] rounded-lg hover:bg-[#f8fafc] disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
